// BookTickets/components/PresaleNotice/index.tsx
'use client'

import { useEffect } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import posthog from 'posthog-js'
import { enGB, es, fr, pt } from 'date-fns/locale'
import type { Locale } from 'date-fns'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'

import type { Presale } from '@booking/types/bookingConfig'
import { lisbonTime, presaleTestParams, type PresaleStatus } from '@booking/lib/presale'
import { format as formatInLisbonTz } from '@booking/lib/date'
import { useBookingExternal } from '@booking/state/external.context'
import type { LocaleKey } from '@/src/i18n/routing'

type PresaleNoticeProps = {
  presale: Presale
  /** The statuses in which the pre-sale link doesn't sell. */
  status: Exclude<PresaleStatus, 'open'>
}

const DATE_FORMATS: Record<LocaleKey, { locale: Locale; day: string; time: string }> = {
  pt: { locale: pt, day: "d 'de' MMMM", time: "HH'h'mm" },
  en: { locale: enGB, day: 'd MMMM', time: 'HH:mm' },
  es: { locale: es, day: "d 'de' MMMM", time: 'HH:mm' },
  fr: { locale: fr, day: 'd MMMM', time: "HH'h'mm" },
}

function formatDay(lisbonWallClock: string, locale: LocaleKey) {
  const date = lisbonTime(lisbonWallClock)
  const { locale: dateLocale, day } = DATE_FORMATS[locale] ?? DATE_FORMATS.pt
  // French writes the first of the month as "1er".
  const pattern = locale === 'fr' && formatInLisbonTz(date, 'd') === '1' ? "d'er' MMMM" : day
  return formatInLisbonTz(date, pattern, { locale: dateLocale })
}

function formatTime(lisbonWallClock: string, locale: LocaleKey) {
  return formatInLisbonTz(
    lisbonTime(lisbonWallClock),
    (DATE_FORMATS[locale] ?? DATE_FORMATS.pt).time,
  )
}

/**
 * The button's link: the normal flow, which from general sale sells the pre-sale's
 * day too, so it then opens on that day. A test link's ?now= and &key= go along,
 * so the normal flow is checked at the same simulated time.
 */
function normalFlowHref(presale: Presale, status: PresaleNoticeProps['status'], locale: LocaleKey) {
  const params = new URLSearchParams(presaleTestParams())
  // Noon, so it's the same day in every time zone (?date= reads a bare date as UTC).
  if (status === 'general') params.set('date', `${presale.sessionDates[0]}T12:00`)
  const query = params.toString()
  return query ? `/${locale}?${query}` : `/${locale}`
}

/**
 * Shown on a pre-sale link outside its window: before the pre-sale opens, after it
 * closes, and from general sale, when the normal flow sells its dates instead.
 */
export default function PresaleNotice({ presale, status }: PresaleNoticeProps) {
  const t = useTranslations('booking')
  const { locale } = useBookingExternal()

  useEffect(() => {
    posthog.capture('presale_notice_viewed', { status })
  }, [status])

  return (
    <div className={styles.notice} data-id="presale-notice">
      <div className={styles.header}>
        <span className={styles.badge}>{t('presale.badge')}</span>
      </div>

      <div className={cn(shared.box, styles.body)}>
        <h2 className={shared.title}>{t(`presale.${status}.title`)}</h2>
        <p className={styles.text}>
          {t(`presale.${status}.text`, {
            sessionDate: formatDay(presale.sessionDates[0], locale),
            opensDate: formatDay(presale.opensAt, locale),
            opensTime: formatTime(presale.opensAt, locale),
            generalSaleDate: formatDay(presale.generalSaleAt, locale),
          })}
        </p>

        {/* A full page load, so the normal flow starts from a clean state. */}
        <a href={normalFlowHref(presale, status, locale)} className={cn(shared.button, styles.cta)}>
          {t(status === 'general' ? 'presale.buyTickets' : 'presale.otherDates')}
        </a>
      </div>
    </div>
  )
}
